import { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { FaEdit, FaEraser, FaFileInvoiceDollar, FaStar, FaTrash } from 'react-icons/fa';
import { API_BASE_URL } from '../config';
import { fetchWithAuth } from '../utils/fetchWithAuth';
import {
    apiCreateBillingSheet,
    apiDeleteBillingSheet,
    apiFetchBillingSheets,
    apiUpdateBillingSheet,
    apiUpdateSheetGLAssignments,
} from '../utils/billingSetupApi';

const BillingSheetsSetup = forwardRef(function BillingSheetsSetup(
    { tenantId, groups, glAccounts = [], canCreate, canUpdate, canDelete, canConfig, saving, setSaving, onReload },
    ref
) {
    const [sheets, setSheets] = useState([]);
    const [careTypes, setCareTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [editingSheet, setEditingSheet] = useState(null);
    const [form, setForm] = useState({
        name: '',
        description: '',
        is_default: false,
        billing_group_ids: [],
        customer_care_type_ids: [],
        // { [groupId]: [glAccountId, ...] } — GL accounts assigned to each group ON THIS SHEET
        glAssignments: {},
    });
    const [errors, setErrors] = useState({});

    // Default GL selection for a group when first added to a sheet: the accounts
    // whose legacy default billing_group_id points at this group.
    const defaultGLsForGroup = (groupId) =>
        glAccounts.filter(a => a.billing_group_id === groupId).map(a => a.id);

    // Seed group→GL maps without placing the same GL under two groups on one sheet.
    const seedAssignmentsWithoutOverlap = (groupIds, claimedIds = new Set()) => {
        const claimed = new Set(claimedIds);
        const glAssignments = {};
        groupIds.forEach(gid => {
            const defaults = defaultGLsForGroup(gid).filter(id => !claimed.has(id));
            defaults.forEach(id => claimed.add(id));
            glAssignments[gid] = defaults;
        });
        return glAssignments;
    };

    // Which group on this sheet currently owns a GL (if any).
    const groupOwningGL = (glAssignments, glId, excludeGroupId = null) => {
        for (const [gid, ids] of Object.entries(glAssignments || {})) {
            if (excludeGroupId != null && String(gid) === String(excludeGroupId)) continue;
            if ((ids || []).includes(glId)) return gid;
        }
        return null;
    };

    const loadSheets = async () => {
        if (!tenantId) return;
        setLoading(true);
        try {
            const [sheetsResult, careTypesRes] = await Promise.all([
                apiFetchBillingSheets(tenantId).catch(err => {
                    console.error('Failed to load billing sheets:', err);
                    return null;
                }),
                fetchWithAuth(`${API_BASE_URL}/customerCareTypes`),
            ]);
            if (sheetsResult) setSheets(sheetsResult);
            if (careTypesRes.ok) {
                const data = await careTypesRes.json();
                // API returns care_type_id / name (and sometimes PascalCase) — normalize to id/name
                const normalized = (Array.isArray(data) ? data : []).map(ct => ({
                    id: ct.id ?? ct.care_type_id ?? ct.CareTypeID,
                    name: ct.name ?? ct.Name,
                })).filter(ct => ct.id != null);
                setCareTypes(normalized);
            } else {
                console.error('Failed to fetch customer care types:', careTypesRes.status);
            }
        } catch (err) {
            console.error('Failed to load billing sheet setup:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { loadSheets(); }, [tenantId]);

    const openAdd = () => {
        setEditingSheet(null);
        const groupIds = groups.map(g => g.id);
        setForm({
            name: '',
            description: '',
            is_default: sheets.length === 0,
            billing_group_ids: groupIds,
            customer_care_type_ids: [],
            glAssignments: seedAssignmentsWithoutOverlap(groupIds),
        });
        setErrors({});
        setShowModal(true);
    };

    useImperativeHandle(ref, () => ({ openAdd }), [sheets.length, groups, glAccounts]);

    const openEdit = (sheet) => {
        setEditingSheet(sheet);
        const glAssignments = {};
        const claimed = new Set();
        (sheet.gl_assignments || [])
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .forEach(a => {
                if (claimed.has(a.gl_account_id)) return;
                claimed.add(a.gl_account_id);
                if (!glAssignments[a.billing_group_id]) glAssignments[a.billing_group_id] = [];
                glAssignments[a.billing_group_id].push(a.gl_account_id);
            });
        setForm({
            name: sheet.name || '',
            description: sheet.description || '',
            is_default: !!sheet.is_default,
            billing_group_ids: [...(sheet.billing_group_ids || [])],
            customer_care_type_ids: [...(sheet.customer_care_type_ids || [])],
            glAssignments,
        });
        setErrors({});
        setShowModal(true);
    };

    const toggleGroup = (groupId) => {
        setForm(prev => {
            const ids = new Set(prev.billing_group_ids);
            const glAssignments = { ...prev.glAssignments };
            if (ids.has(groupId)) {
                ids.delete(groupId);
                delete glAssignments[groupId];
            } else {
                ids.add(groupId);
                // Seed with default GLs when a group is first added — skip GLs
                // already claimed by another group on this sheet.
                if (!glAssignments[groupId]) {
                    const claimed = new Set(
                        Object.entries(glAssignments)
                            .flatMap(([, glIds]) => glIds || [])
                    );
                    glAssignments[groupId] = defaultGLsForGroup(groupId).filter(id => !claimed.has(id));
                }
            }
            return { ...prev, billing_group_ids: [...ids], glAssignments };
        });
    };

    // Checking a GL under a group moves it from any other group on this sheet.
    const toggleGLAssignment = (groupId, glId) => {
        setForm(prev => {
            const isChecked = (prev.glAssignments[groupId] || []).includes(glId);
            const next = {};
            for (const [gid, ids] of Object.entries(prev.glAssignments)) {
                next[gid] = (ids || []).filter(id => id !== glId);
            }
            if (!isChecked) {
                next[groupId] = [...(next[groupId] || []), glId];
            } else if (!next[groupId]) {
                next[groupId] = [];
            }
            return { ...prev, glAssignments: next };
        });
    };

    const clearGLsForGroup = (groupId) => {
        setForm(prev => ({
            ...prev,
            glAssignments: { ...prev.glAssignments, [groupId]: [] },
        }));
    };

    const clearAllGLAssignments = () => {
        setForm(prev => {
            const cleared = {};
            prev.billing_group_ids.forEach(gid => { cleared[gid] = []; });
            return { ...prev, glAssignments: cleared };
        });
    };

    const handleClearSheetGLs = async (sheet) => {
        if (!canConfig) return;
        const count = (sheet.gl_assignments || []).length;
        if (count === 0) {
            alert('This sheet has no GL assignments to clear.');
            return;
        }
        if (!window.confirm(`Clear all ${count} GL assignment(s) on “${sheet.name}”? Groups and care types stay unchanged.`)) {
            return;
        }
        setSaving(true);
        try {
            await apiUpdateSheetGLAssignments(sheet.id, tenantId, []);
            await loadSheets();
            if (onReload) await onReload();
        } catch (err) {
            alert('Failed to clear GL assignments: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const glLabel = (glId) => {
        const a = glAccounts.find(x => x.id === glId);
        return a ? `${a.code}` : glId;
    };

    const sheetGroupsWithGLs = (sheet) => {
        const byGroup = {};
        (sheet.billing_group_ids || []).forEach(gid => { byGroup[gid] = []; });
        (sheet.gl_assignments || [])
            .slice()
            .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
            .forEach(a => {
                if (!byGroup[a.billing_group_id]) byGroup[a.billing_group_id] = [];
                byGroup[a.billing_group_id].push(a.gl_account_id);
            });
        return byGroup;
    };

    const toggleCareType = (careTypeId) => {
        setForm(prev => {
            const ids = new Set(prev.customer_care_type_ids.map(String));
            const key = String(careTypeId);
            if (ids.has(key)) ids.delete(key);
            else ids.add(key);
            return { ...prev, customer_care_type_ids: [...ids].map(Number) };
        });
    };

    const buildAssignments = () => {
        const assignments = [];
        form.billing_group_ids.forEach(gid => {
            (form.glAssignments[gid] || []).forEach((glId, i) => {
                assignments.push({ billing_group_id: gid, gl_account_id: glId, sort_order: i });
            });
        });
        return assignments;
    };

    const handleSave = async () => {
        const errs = {};
        if (!form.name.trim()) errs.name = 'Name is required';
        if (form.billing_group_ids.length === 0) errs.billing_group_ids = 'Select at least one billing group';
        if (form.customer_care_type_ids.length === 0) errs.customer_care_type_ids = 'Select at least one care type';
        if (Object.keys(errs).length > 0) {
            setErrors(errs);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                name: form.name.trim(),
                description: form.description.trim(),
                is_default: form.is_default,
                billing_group_ids: form.billing_group_ids,
                customer_care_type_ids: form.customer_care_type_ids,
            };
            let sheetId;
            if (editingSheet) {
                await apiUpdateBillingSheet(editingSheet.id, payload);
                sheetId = editingSheet.id;
            } else {
                const created = await apiCreateBillingSheet(tenantId, { ...payload, sort_order: sheets.length });
                sheetId = created.id;
            }
            // GL group assignments are configuration — only writable with config_manage
            if (canConfig && sheetId) {
                await apiUpdateSheetGLAssignments(sheetId, tenantId, buildAssignments());
            }
            setShowModal(false);
            await loadSheets();
            if (onReload) await onReload();
        } catch (err) {
            alert('Failed to save billing sheet: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this billing sheet configuration?')) return;
        setSaving(true);
        try {
            await apiDeleteBillingSheet(id, tenantId);
            await loadSheets();
        } catch (err) {
            alert('Failed to delete billing sheet: ' + err.message);
        } finally {
            setSaving(false);
        }
    };

    const groupName = (id) => groups.find(g => g.id === id)?.name || id;
    const careTypeName = (id) => careTypes.find(c => String(c.id) === String(id))?.name || `Type ${id}`;

    const showActions = canUpdate || canDelete || canConfig;
    const canSaveModal = editingSheet ? canUpdate : canCreate;

    if (loading && sheets.length === 0) {
        return (
            <div style={{ padding: '40px', textAlign: 'center', color: 'var(--theme-text-muted)' }}>
                <Spinner animation="border" size="sm" style={{ marginRight: '8px' }} />
                Loading billing sheets…
            </div>
        );
    }

    return (
        <div>
            <p style={{ color: 'var(--theme-text-secondary)', fontSize: '0.9rem', marginBottom: '20px' }}>
                Configure named billing sheets by choosing which billing groups appear as columns, which GL
                accounts sit under each group on this sheet, and which care types are included as residents.
                Each GL account can only be assigned once per sheet (under one group); the same GL may sit
                under a different group on another sheet.
            </p>

            {sheets.length === 0 ? (
                <div style={{
                    textAlign: 'center', padding: '48px 24px',
                    backgroundColor: 'var(--theme-surface)', borderRadius: '10px', border: '1px dashed #d1d5db',
                }}>
                    <FaFileInvoiceDollar style={{ fontSize: '2rem', color: 'var(--theme-text-muted)', marginBottom: '12px' }} />
                    <p style={{ color: 'var(--theme-text-secondary)', marginBottom: '16px' }}>No billing sheets configured yet.</p>
                    {canCreate && (
                        <Button variant="dark" onClick={openAdd} disabled={saving || groups.length === 0}>
                            Create Billing Sheet
                        </Button>
                    )}
                    {groups.length === 0 && (
                        <p style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem', marginTop: '12px' }}>
                            Add billing groups first before creating a sheet.
                        </p>
                    )}
                </div>
            ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                    {sheets.map(sheet => {
                        const grouped = sheetGroupsWithGLs(sheet);
                        const glCount = (sheet.gl_assignments || []).length;
                        return (
                            <div
                                key={sheet.id}
                                style={{
                                    backgroundColor: 'var(--theme-surface)',
                                    border: '1px solid var(--theme-border)',
                                    borderRadius: '10px',
                                    padding: '16px 18px',
                                }}
                            >
                                <div className="d-flex justify-content-between align-items-start gap-3 flex-wrap">
                                    <div style={{ minWidth: 0, flex: 1 }}>
                                        <div className="d-flex align-items-center gap-2 mb-1">
                                            <span style={{ fontWeight: 700, fontSize: '1rem', color: 'var(--theme-text)' }}>
                                                {sheet.name}
                                            </span>
                                            {sheet.is_default && (
                                                <span title="Default sheet" style={{ color: '#d97706', fontSize: '0.85rem' }}>
                                                    <FaStar /> Default
                                                </span>
                                            )}
                                        </div>
                                        {sheet.description && (
                                            <div style={{ fontSize: '0.85rem', color: 'var(--theme-text-secondary)', marginBottom: '8px' }}>
                                                {sheet.description}
                                            </div>
                                        )}
                                        <div style={{ fontSize: '0.8rem', color: 'var(--theme-text-muted)', marginBottom: '12px' }}>
                                            {(sheet.customer_care_type_ids || []).length} care type(s)
                                            {' · '}
                                            {glCount} GL placement(s)
                                        </div>

                                        {(sheet.billing_group_ids || []).length === 0 ? (
                                            <div style={{ fontSize: '0.85rem', color: 'var(--theme-text-muted)' }}>
                                                No billing groups on this sheet.
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                                                {(sheet.billing_group_ids || []).map(gid => {
                                                    const glIds = grouped[gid] || [];
                                                    return (
                                                        <div
                                                            key={gid}
                                                            style={{
                                                                border: '1px solid var(--theme-border)',
                                                                borderRadius: '8px',
                                                                padding: '10px 12px',
                                                                backgroundColor: 'var(--theme-surface-muted, #f8fafc)',
                                                            }}
                                                        >
                                                            <div style={{ fontWeight: 650, fontSize: '0.85rem', marginBottom: '6px' }}>
                                                                {groupName(gid)}
                                                            </div>
                                                            {glIds.length === 0 ? (
                                                                <div style={{ fontSize: '0.78rem', color: 'var(--theme-text-muted)' }}>
                                                                    No GLs assigned
                                                                </div>
                                                            ) : (
                                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                                    {glIds.map(glId => (
                                                                        <span
                                                                            key={glId}
                                                                            style={{
                                                                                fontSize: '0.72rem',
                                                                                fontFamily: 'monospace',
                                                                                backgroundColor: 'var(--theme-surface)',
                                                                                border: '1px solid var(--theme-border)',
                                                                                borderRadius: '4px',
                                                                                padding: '2px 6px',
                                                                            }}
                                                                            title={glAccounts.find(a => a.id === glId)?.name || ''}
                                                                        >
                                                                            {glLabel(glId)}
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {(sheet.customer_care_type_ids || []).length > 0 && (
                                            <div style={{ marginTop: '10px', fontSize: '0.8rem', color: 'var(--theme-text-secondary)' }}>
                                                Care types:{' '}
                                                {(sheet.customer_care_type_ids || []).map(careTypeName).join(', ')}
                                            </div>
                                        )}
                                    </div>

                                    {showActions && (
                                        <div className="d-flex gap-2 flex-shrink-0">
                                            {canUpdate && (
                                                <Button variant="outline-secondary" size="sm" onClick={() => openEdit(sheet)} disabled={saving} title="Edit sheet">
                                                    <FaEdit />
                                                </Button>
                                            )}
                                            {canConfig && (
                                                <Button
                                                    variant="outline-warning"
                                                    size="sm"
                                                    onClick={() => handleClearSheetGLs(sheet)}
                                                    disabled={saving || glCount === 0}
                                                    title="Clear all GL assignments on this sheet"
                                                >
                                                    <FaEraser />
                                                </Button>
                                            )}
                                            {canDelete && (
                                                <Button variant="outline-danger" size="sm" onClick={() => handleDelete(sheet.id)} disabled={saving} title="Delete sheet">
                                                    <FaTrash />
                                                </Button>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
                <Modal.Header closeButton>
                    <Modal.Title>{editingSheet ? 'Edit Billing Sheet' : 'New Billing Sheet'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                            value={form.name}
                            onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                            isInvalid={!!errors.name}
                        />
                        <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description (optional)</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={form.description}
                            onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                        />
                    </Form.Group>
                    <Form.Check
                        type="checkbox"
                        id="sheet-default"
                        className="mb-4"
                        label="Use as default billing sheet"
                        checked={form.is_default}
                        onChange={e => setForm(f => ({ ...f, is_default: e.target.checked }))}
                    />

                    <Form.Label className="fw-semibold">Billing groups to include</Form.Label>
                    {errors.billing_group_ids && (
                        <div className="text-danger small mb-2">{errors.billing_group_ids}</div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '20px' }}>
                        {groups.map(g => (
                            <Form.Check
                                key={g.id}
                                type="checkbox"
                                id={`group-${g.id}`}
                                label={g.name}
                                checked={form.billing_group_ids.includes(g.id)}
                                onChange={() => toggleGroup(g.id)}
                            />
                        ))}
                    </div>

                    {/* Per-sheet GL account assignment for each selected group */}
                    {form.billing_group_ids.length > 0 && (
                        <>
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                                <Form.Label className="fw-semibold mb-0">GL accounts per group (on this sheet)</Form.Label>
                                {canConfig && (
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        onClick={clearAllGLAssignments}
                                        disabled={Object.values(form.glAssignments).every(ids => !(ids || []).length)}
                                    >
                                        <FaEraser style={{ marginRight: '5px' }} />
                                        Clear all GLs
                                    </Button>
                                )}
                            </div>
                            <div className="text-muted small mb-2">
                                A GL can only appear under one group on this sheet. Checking it under another
                                group moves it.
                            </div>
                            {!canConfig && (
                                <div className="text-muted small mb-2">
                                    You need billing configuration permission to change GL assignments.
                                </div>
                            )}
                            <div style={{ marginBottom: '20px' }}>
                                {groups.filter(g => form.billing_group_ids.includes(g.id)).map(g => (
                                    <div key={g.id} style={{
                                        border: '1px solid var(--theme-border)', borderRadius: '8px',
                                        padding: '12px 14px', marginBottom: '10px',
                                    }}>
                                        <div className="d-flex justify-content-between align-items-center mb-2">
                                            <div style={{ fontWeight: 600 }}>{g.name}</div>
                                            {canConfig && (form.glAssignments[g.id] || []).length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => clearGLsForGroup(g.id)}
                                                    style={{
                                                        border: 'none', background: 'transparent',
                                                        color: 'var(--theme-text-muted)', fontSize: '0.78rem',
                                                        cursor: 'pointer', padding: 0,
                                                    }}
                                                >
                                                    Clear group
                                                </button>
                                            )}
                                        </div>
                                        {glAccounts.length === 0 ? (
                                            <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>
                                                No GL accounts defined yet.
                                            </span>
                                        ) : (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                                                {glAccounts.map(a => {
                                                    const ownerGroupId = groupOwningGL(form.glAssignments, a.id, g.id);
                                                    const ownerName = ownerGroupId ? groupName(ownerGroupId) : null;
                                                    const checked = (form.glAssignments[g.id] || []).includes(a.id);
                                                    return (
                                                        <Form.Check
                                                            key={`${g.id}-${a.id}`}
                                                            type="checkbox"
                                                            id={`gl-${g.id}-${a.id}`}
                                                            label={
                                                                ownerName && !checked
                                                                    ? `${a.code} — ${a.name} (in ${ownerName})`
                                                                    : `${a.code} — ${a.name}`
                                                            }
                                                            checked={checked}
                                                            onChange={() => toggleGLAssignment(g.id, a.id)}
                                                            disabled={!canConfig}
                                                            title={
                                                                ownerName && !checked
                                                                    ? `Currently under ${ownerName}. Checking moves it here.`
                                                                    : undefined
                                                            }
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}

                    <Form.Label className="fw-semibold">Care types to include</Form.Label>
                    {errors.customer_care_type_ids && (
                        <div className="text-danger small mb-2">{errors.customer_care_type_ids}</div>
                    )}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {careTypes.map(ct => (
                            <Form.Check
                                key={ct.id}
                                type="checkbox"
                                id={`care-${ct.id}`}
                                label={ct.name}
                                checked={form.customer_care_type_ids.map(String).includes(String(ct.id))}
                                onChange={() => toggleCareType(ct.id)}
                            />
                        ))}
                        {careTypes.length === 0 && (
                            <span style={{ color: 'var(--theme-text-muted)', fontSize: '0.85rem' }}>No care types found.</span>
                        )}
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
                    <Button variant="dark" onClick={handleSave} disabled={saving || !canSaveModal}>
                        {saving ? 'Saving…' : (editingSheet ? 'Save Changes' : 'Create Sheet')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
});

export default BillingSheetsSetup;
